<script setup lang="ts">
import { ref, computed } from 'vue'
import {
  state,
  setActiveDoc,
  setPage,
  removeDocument,
  removeSnippetsForDocument,
  snippetCountForDoc,
  snippetCountForPage,
} from '../lib/store'
import type { SnipDocument } from '../types'

type SortKey = 'name-asc' | 'name-desc' | 'date-asc' | 'date-desc'

const open = ref(true)
const query = ref('')
const sortBy = ref<SortKey>('date-desc')
const onlySnipped = ref(false)
const expanded = ref<Set<string>>(new Set())
const confirming = ref<string | null>(null) // docId pending delete confirm

const docs = computed(() => {
  let list = [...state.documents]
  const q = query.value.trim().toLowerCase()
  if (q) list = list.filter((d) => d.name.toLowerCase().includes(q))
  if (onlySnipped.value) list = list.filter((d) => snippetCountForDoc(d.id) > 0)
  list.sort((a, b) => {
    switch (sortBy.value) {
      case 'name-asc': return a.name.localeCompare(b.name)
      case 'name-desc': return b.name.localeCompare(a.name)
      case 'date-asc': return a.dateAdded - b.dateAdded
      default: return b.dateAdded - a.dateAdded
    }
  })
  return list
})

function toggle(id: string) {
  const next = new Set(expanded.value)
  next.has(id) ? next.delete(id) : next.add(id)
  expanded.value = next
}

function pickDoc(d: SnipDocument) {
  setActiveDoc(d.id)
  if (!expanded.value.has(d.id)) toggle(d.id)
}

function pickPage(d: SnipDocument, page: number) {
  if (state.activeDocId !== d.id) setActiveDoc(d.id)
  setPage(page)
}

function pagesOf(d: SnipDocument): number[] {
  const all = Array.from({ length: d.pageCount }, (_, i) => i + 1)
  return onlySnipped.value ? all.filter((p) => snippetCountForPage(d.id, p) > 0) : all
}

function confirmDelete(d: SnipDocument) {
  removeSnippetsForDocument(d.id)
  removeDocument(d.id)
  confirming.value = null
}
</script>

<template>
  <div class="tree" :class="{ collapsed: !open }">
    <button class="hdr" @click="open = !open">
      <span>{{ open ? '▾' : '▸' }}</span>
      <strong>Documents</strong>
      <span class="count">{{ state.documents.length }}</span>
    </button>

    <template v-if="open">
      <div class="controls">
        <input v-model="query" class="search" placeholder="Search documents…" />
        <select v-model="sortBy" class="sort" title="Sort">
          <option value="date-desc">Newest</option>
          <option value="date-asc">Oldest</option>
          <option value="name-asc">Name A–Z</option>
          <option value="name-desc">Name Z–A</option>
        </select>
        <label class="only" title="Show only documents/pages with snippets">
          <input type="checkbox" v-model="onlySnipped" /> snipped
        </label>
      </div>

      <div class="list scroll">
        <p v-if="!docs.length" class="empty muted">No documents.</p>
        <div v-for="d in docs" :key="d.id" class="doc">
          <div class="doc-row" :class="{ active: d.id === state.activeDocId }">
            <button class="caret" @click="toggle(d.id)">{{ expanded.has(d.id) ? '▾' : '▸' }}</button>
            <button class="doc-name" :title="d.name" @click="pickDoc(d)">
              <span class="nm">{{ d.name }}</span>
              <span class="meta">{{ d.pageCount }}p</span>
              <span v-if="snippetCountForDoc(d.id)" class="badge">{{ snippetCountForDoc(d.id) }}</span>
            </button>
            <button class="trash" title="Delete document" @click="confirming = d.id">🗑</button>
          </div>

          <div v-if="confirming === d.id" class="confirm">
            Delete “{{ d.name }}” and its snippets?
            <button class="yes" @click="confirmDelete(d)">Delete</button>
            <button class="no" @click="confirming = null">Cancel</button>
          </div>

          <div v-if="expanded.has(d.id) || d.id === state.activeDocId" class="pages">
            <button
              v-for="p in pagesOf(d)"
              :key="p"
              class="page"
              :class="{ active: d.id === state.activeDocId && p === state.activePage }"
              @click="pickPage(d, p)"
            >
              Page {{ p }}
              <span v-if="snippetCountForPage(d.id, p)" class="badge">{{ snippetCountForPage(d.id, p) }}</span>
            </button>
            <p v-if="!pagesOf(d).length" class="empty muted">No snipped pages.</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.tree {
  border-bottom: 1px solid var(--line);
  background: var(--bg);
  display: flex;
  flex-direction: column;
  max-height: 40%;
}
.tree.collapsed { max-height: none; }
.hdr {
  border: none; border-radius: 0;
  display: flex; align-items: center; gap: 6px;
  width: 100%; padding: 6px 8px; background: var(--bg-soft);
}
.hdr:hover { background: var(--brand-50); }
.count { margin-left: auto; background: var(--brand); color: #fff; border-radius: 10px; padding: 0 7px; font-size: 11px; }
.controls { display: flex; gap: 4px; padding: 5px 8px; border-bottom: 1px solid var(--line); align-items: center; }
.search { flex: 1; min-width: 0; padding: 3px 6px; border: 1px solid var(--line); border-radius: 5px; font-size: 12px; }
.sort { padding: 3px 4px; border: 1px solid var(--line); border-radius: 5px; font-size: 11px; }
.only { font-size: 10px; color: var(--muted); display: flex; align-items: center; gap: 2px; white-space: nowrap; }
.list { overflow: auto; padding: 3px; }
.empty { padding: 8px; text-align: center; }
.doc-row { display: flex; align-items: center; border-radius: 4px; }
.doc-row.active { background: var(--brand-50); }
.caret { border: none; background: none; padding: 2px 4px; color: var(--muted); }
.doc-name { border: none; background: none; flex: 1; min-width: 0; display: flex; align-items: center; gap: 6px; padding: 4px 2px; text-align: left; }
.doc-name:hover { background: var(--brand-50); }
.nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.meta { color: var(--muted); font-size: 10px; flex: none; }
.badge { background: var(--brand); color: #fff; border-radius: 8px; padding: 0 5px; font-size: 10px; flex: none; }
.trash { border: none; background: none; padding: 0 6px; opacity: 0.5; }
.trash:hover { opacity: 1; background: none; }
.confirm { font-size: 11px; padding: 4px 8px; background: #fdeeee; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.confirm .yes { border-color: var(--danger); color: var(--danger); padding: 2px 8px; }
.confirm .no { padding: 2px 8px; }
.pages { padding: 2px 0 4px 22px; display: flex; flex-direction: column; gap: 1px; }
.page { border: none; background: none; text-align: left; padding: 3px 6px; border-radius: 4px; display: flex; align-items: center; gap: 6px; font-size: 12px; }
.page:hover { background: var(--brand-50); }
.page.active { background: var(--brand); color: #fff; }
.page.active .badge { background: #fff; color: var(--brand); }
</style>
